// Cola de reintento para leads que no se pudieron enviar al backend.
//
// Por qué existe: antes, si el POST /api/video-lead fallaba (backend dormido,
// red del celular, timeout), la persona veía el contenido igual y su lead no
// quedaba en ninguna parte: ni en el Sheet, ni en Meta, ni en el correo. Ahora
// el payload se guarda y se reintenta al cargar cualquier página del sitio.
//
// Trade-off asumido: si el POST sí llegó al servidor pero la respuesta se perdió,
// el reintento crea una fila repetida en el Sheet. Se prefiere un lead duplicado
// (visible, se limpia a mano) antes que un lead perdido en silencio.

const STORAGE_KEY = 'pendingLeads';
const MAX_QUEUED = 20;
const MAX_ATTEMPTS = 8;
const REQUEST_TIMEOUT_MS = 45000; // El backend en Render puede tardar en despertar

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_QUEUED)));
  } catch {
    // noop
  }
}

/** Guarda un lead que no se pudo enviar para reintentarlo más tarde. */
export function enqueueLead({ endpoint, payload }) {
  const queue = readQueue();
  queue.push({
    endpoint,
    payload,
    attempts: 0,
    queued_at: Date.now(),
  });
  writeQueue(queue);
  console.warn('[leadQueue] lead guardado para reintento', { endpoint, total: queue.length });
}

function postWithTimeout(url, payload) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    ...(controller ? { signal: controller.signal } : {}),
  }).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

/**
 * Envía un lead al backend. Si falla, lo deja en la cola para reintentar.
 * Devuelve true solo si el backend confirmó el guardado.
 */
export async function sendLeadWithRetry(backendUrl, endpoint, payload) {
  const base = (backendUrl || 'http://localhost:3001').replace(/\/$/, '');
  try {
    const response = await postWithTimeout(`${base}${endpoint}`, payload);
    if (response.ok) return true;
    // 4xx = payload inválido, reintentar no lo va a arreglar. Solo se encolan los 5xx.
    if (response.status >= 500) enqueueLead({ endpoint, payload });
    else console.warn('[leadQueue] backend rechazó el lead', response.status);
    return false;
  } catch (err) {
    enqueueLead({ endpoint, payload });
    return false;
  }
}

/** Reintenta los leads pendientes. Se llama al arrancar la app. */
export async function flushLeadQueue(backendUrl) {
  const queue = readQueue();
  if (queue.length === 0) return;

  const base = (backendUrl || 'http://localhost:3001').replace(/\/$/, '');
  const pendientes = [];

  for (const item of queue) {
    if (item.attempts >= MAX_ATTEMPTS) {
      console.warn('[leadQueue] lead descartado tras', MAX_ATTEMPTS, 'intentos', item.payload?.email);
      continue;
    }
    try {
      const response = await postWithTimeout(`${base}${item.endpoint}`, item.payload);
      if (response.ok) {
        console.log('[leadQueue] lead pendiente recuperado', item.payload?.email);
        continue;
      }
      if (response.status >= 500) pendientes.push({ ...item, attempts: item.attempts + 1 });
    } catch {
      pendientes.push({ ...item, attempts: item.attempts + 1 });
    }
  }

  writeQueue(pendientes);
}
